import React from "react";
import "../../pages/Provider/orderKit/orderKit.scss";
import { fetchData, SubmitData } from "../../services/apiService";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import useAlertUtils, { validateUSPhoneNumber } from "../../utils/common.utils";
import { decryptedData, encryptData } from "../../services/encryptionDecription.service";
import { trackPaymentCompleted } from "../../utils/gtmTracking";

function UserKitCheckout() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [showPayModal, setShowPayModal] = React.useState(false);
  const paypalRef = React.useRef(null);
  const [isFinalizingPayment, setIsFinalizingPayment] = React.useState(false);
  const [personalInfo, setPersonalInfo] = React.useState(null);
  const [isLoadingPersonal, setIsLoadingPersonal] = React.useState(false);
  const [shipTo, setShipTo] = React.useState({
    attentionTo: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    region: "",
    postalCode: "",
    countryCode: "USA",
    contactPhoneNumber: "",
    trackingEmail: "",
  });

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const tokenData = JSON.parse(atob(token.split(".")[1]));
      return tokenData.userId || tokenData.id || null;
    } catch (e) {
      return null;
    }
  };

  const fetchPersonalInfo = async (userId) => {
    if (!userId) return;
    setIsLoadingPersonal(true);
    try {
      const response = await fetchData(
        "/api/intakeForms/pearsonalInformation?userId=" +
          btoa(encryptData(userId.toString()))
      );
      const pi = response?.data || null;
      setPersonalInfo(pi);
      
      if (pi) {
        let phone = "";
        if (pi.mobileNumber) {
          const phoneCandidate = decryptedData(pi.mobileNumber) || "";
          const validation = validateUSPhoneNumber(phoneCandidate);
          if (validation.isValid) phone = phoneCandidate;
        }

        setShipTo((prev) => ({
          ...prev,
          attentionTo: `${decryptedData(pi.firstName) || ""} ${
            decryptedData(pi.lastName) || ""
          }`.trim(),
          addressLine1: decryptedData(pi.address1) || "",
          addressLine2: decryptedData(pi.address2) || "",
          city: decryptedData(pi.city) || "",
          region: decryptedData(pi.state) || "",
          postalCode: decryptedData(pi.zipCode) || "",
          countryCode: "USA",
          contactPhoneNumber: phone,
          trackingEmail: decryptedData(pi.email) || prev.trackingEmail || "",
        }));
      }
    } catch (e) {
      console.error("Error fetching personal info:", e);
      setPersonalInfo(null);
    } finally {
      setIsLoadingPersonal(false);
    }
  };

  const fetchPersonalInfoAndReturn = async (userId) => {
    if (!userId) return null;
    try {
      const response = await fetchData(
        "/api/intakeForms/pearsonalInformation?userId=" +
          btoa(encryptData(userId.toString()))
      );
      const pi = response?.data || null;
      setPersonalInfo(pi);
      return pi;
    } catch (e) {
      console.error("Error fetching personal info:", e);
      setPersonalInfo(null);
      return null;
    }
  };

  const loadOrder = React.useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      let res = await fetchData(
        `/api/kit-orders/get-kit-order-detail/:id/${orderId}`
      );
      let found = res?.data || null;
      if (!found) {
        const userId = getUserIdFromToken();
        if (userId) {
          res = await fetchData(
            `/api/kit-orders/get-user-kit-orders/${userId}`
          );
          const list = res?.data?.items || res?.data || [];
          found =
            (Array.isArray(list) ? list : []).find(
              (o) => String(o.id) === String(orderId)
            ) || null;
        }
      }
      setOrder(found);
      
      // Fetch personal info to prefill form
      const userId = getUserIdFromToken();
      let personalInfoData = null;
      if (userId) {
        personalInfoData = await fetchPersonalInfoAndReturn(userId);
      }
      
      if (found) {
        const phoneFromAPI = found.contactNo || found.contactPhoneNumber || "";
        let validatedPhone = "";
        if (phoneFromAPI) {
          const phoneValidation = validateUSPhoneNumber(phoneFromAPI);
          if (phoneValidation.isValid) {
            validatedPhone = phoneFromAPI;
          }
        }

        // Use personal info if available, otherwise use order data
        if (personalInfoData) {
          let phone = "";
          if (personalInfoData.mobileNumber) {
            const phoneCandidate = decryptedData(personalInfoData.mobileNumber) || "";
            const validation = validateUSPhoneNumber(phoneCandidate);
            if (validation.isValid) phone = phoneCandidate;
          }

          setShipTo({
            attentionTo: `${decryptedData(personalInfoData.firstName) || ""} ${
              decryptedData(personalInfoData.lastName) || ""
            }`.trim(),
            addressLine1: decryptedData(personalInfoData.address1) || "",
            addressLine2: decryptedData(personalInfoData.address2) || "",
            city: decryptedData(personalInfoData.city) || "",
            region: decryptedData(personalInfoData.state) || "",
            postalCode: decryptedData(personalInfoData.zipCode) || "",
            countryCode: "USA",
            contactPhoneNumber: phone || validatedPhone,
            trackingEmail: decryptedData(personalInfoData.email) || found.trackingEmail || "",
          });
        } else {
          // If no personal info, use order data
          setShipTo({
            attentionTo: found.attentionTo || "",
            addressLine1: found.addressLine1 || "",
            addressLine2: found.addressLine2 || "",
            city: found.city || "",
            region: found.region || "",
            postalCode: found.postalCode || "",
            countryCode: "USA",
            contactPhoneNumber: validatedPhone,
            trackingEmail: found.trackingEmail || "",
          });
        }
      }
    } catch (e) {
      setError("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  React.useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const subtotal = React.useMemo(() => {
    try {
      const kits =
        typeof order?.kits === "string"
          ? JSON.parse(order.kits)
          : order?.kits || [];
      const sum = (kits || []).reduce(
        (acc, k) => acc + (Number(k.totalPrice) || 0),
        0
      );
      return Number.isFinite(sum) ? sum : Number(order?.total) || 0;
    } catch {
      return Number(order?.total) || 0;
    }
  }, [order]);

  const total = subtotal;

  const parseKits = () => {
    try {
      return typeof order?.kits === "string"
        ? JSON.parse(order.kits)
        : order?.kits || [];
    } catch {
      return [];
    }
  };

  const parsePanels = () => {
    const kits = parseKits();
    const panels = [];
    kits.forEach((k) => {
      if (Array.isArray(k.panels)) {
        k.panels.forEach((p) => panels.push(p));
      }
    });
    return panels;
  };

  const hasAddress = React.useMemo(() => {
    const s = shipTo;
    return !!(
      s.attentionTo &&
      s.addressLine1 &&
      s.city &&
      s.region &&
      s.postalCode &&
      (s.trackingEmail || order?.trackingEmail)
    );
  }, [shipTo, order]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setShipTo((prev) => ({ ...prev, [name]: value }));
  };

  const saveAddressLocally = () => {
    localStorage.setItem(
      `user_kit_checkout_addr_${orderId}`,
      JSON.stringify(shipTo)
    );
  };

  const { alertSuccess, alertError } = useAlertUtils();
  const [isPaying, setIsPaying] = React.useState(false);
  const isAddressComplete = React.useMemo(() => {
    return (
      shipTo.attentionTo &&
      shipTo.addressLine1 &&
      shipTo.city &&
      shipTo.region &&
      shipTo.postalCode &&
      (shipTo.trackingEmail || order?.trackingEmail)
    );
  }, [shipTo, order]);

  const ensurePaypalForHost = async (host) => {
    const selector = 'script[src*="paypal.com/sdk/js"]';
    const existingScripts = Array.from(document.querySelectorAll(selector));
    const hasCorrectHost = existingScripts.some((s) => s.src.includes(host));
    const hasAuthorize = existingScripts.some((s) =>
      s.src.includes("intent=authorize")
    );
    if (window.paypal && hasCorrectHost && hasAuthorize) return window.paypal;
    existingScripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    try {
      delete window.paypal;
    } catch (_) {
      window.paypal = undefined;
    }
    const clientId = process.env.REACT_APP_PAYPAL_C_CLIENT_KEY;
    if (!clientId || clientId === "sb") return null;
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://${host}/sdk/js?client-id=${clientId}&intent=authorize&components=buttons&commit=true&enable-funding=card`;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
    return window.paypal;
  };

  const startUserPayment = async () => {
    try {
      if (!isAddressComplete) {
        alertError("Please complete shipping address before paying.");
        return;
      }

      const phoneValidation = validateUSPhoneNumber(shipTo.contactPhoneNumber);
      if (!phoneValidation.isValid) {
        alertError(phoneValidation.error);
        return;
      }

      setIsPaying(true);

      const kitsPayload =
        typeof order?.kits === "string"
          ? order.kits
          : JSON.stringify(parseKits());

      const payBody = {
        userID: order?.userID || null,
        providerId: order?.providerId || null,
        buyerAccountId: 1163719628,
        providerContactAccountId: null,
        shippingMethodCode: order?.shippingMethodCode || null,
        attentionTo: shipTo.attentionTo || null,
        addressLine1: shipTo.addressLine1 || null,
        addressLine2: shipTo.addressLine2 || null,
        addressLine3: "",
        city: shipTo.city || null,
        region: shipTo.region || null,
        postalCode: shipTo.postalCode || null,
        countryCode: "USA",
        contactNo: shipTo.contactPhoneNumber || null,
        trackingEmail: shipTo.trackingEmail || order?.trackingEmail || null,
        isResidence: true,
        kits: kitsPayload,
        total: Number(order?.total) || total,
      };

      const createRes = await SubmitData(
        "/api/kit-orders/kit-payment/authorize",
        {
          totalPrice: Number(payBody.total.toFixed(2)),
          description: "ZRT Kit Order",
        }
      );
      const paypalOrderId = createRes?.data?.id || createRes?.id;
      const links = createRes?.data?.links || createRes?.links || [];
      if (!paypalOrderId) {
        alertError("Payment could not be initiated.");
        setIsPaying(false);
        return;
      }

      const approveLink =
        (links.find((l) => l.rel === "approve") || {}).href || "";
      const host = approveLink.includes("sandbox.paypal.com")
        ? "www.sandbox.paypal.com"
        : "www.paypal.com";
      const paypalLib = await ensurePaypalForHost(host);
      if (!paypalLib) {
        alertError("PayPal client id is not configured.");
        setIsPaying(false);
        return;
      }
      setShowPayModal(true);
      setTimeout(() => {
        if (!paypalRef.current) {
          setShowPayModal(false);
          setIsPaying(false);
          return;
        }
        try {
          paypalRef.current.innerHTML = "";
          const config = {
            createOrder: () => paypalOrderId,
            onApprove: async (data) => {
              try {
                setIsFinalizingPayment(true);

                const authorizeResp = await SubmitData(
                  "/api/kit-orders/kit-payment/authorize-order",
                  {
                    paypalOrderId: data.orderID,
                  }
                );

                if (!authorizeResp?.success) {
                  alertError(
                    authorizeResp?.message || "Payment authorization failed."
                  );
                  return;
                }

                const authorizationId = authorizeResp?.data?.authorizationId;

                if (!authorizationId) {
                  alertError("Authorization ID not found in PayPal response.");
                  return;
                }

                const res = await SubmitData(`/api/kit-orders/pay/${orderId}`, {
                  ...payBody,
                  paypalOrderID: String(authorizationId),
                });

                if (res?.success) {
                  // Track payment completed event for Google Tag Manager
                  trackPaymentCompleted();
                  alertSuccess("Payment authorized!");
                  setShowPayModal(false);
                  navigate("/app/user-kit-orders");
                } else {
                  alertError(
                    res?.message || "Authorization failed. Please try again."
                  );
                }
              } catch (err) {
                console.error("Payment authorization error:", err);
                alertError("Payment authorization failed. Please try again.");
              } finally {
                setIsPaying(false);
                setIsFinalizingPayment(false);
              }
            },
            onCancel: () => {
              if (!isFinalizingPayment) {
                setShowPayModal(false);
                setIsPaying(false);
              }
            },
            onError: () => {
              alertError("Payment initialization failed.");
              if (!isFinalizingPayment) {
                setShowPayModal(false);
                setIsPaying(false);
              }
            },
          };
          paypalLib.Buttons(config).render(paypalRef.current);
        } catch (_) {
          setShowPayModal(false);
          setIsPaying(false);
        }
      }, 0);
    } catch (_) {
      alertError("Payment setup failed.");
      setIsPaying(false);
    }
  };

  const handlePayNow = async () => {
    await startUserPayment();
  };

  return (
    <div className="layout">
      <Header />
      <div className="checkoutPage">
        <div className="checkoutHeader">
          <div className="checkoutHeaderLeft">
            <button className="backBtn" onClick={() => navigate(-1)}>
              ←
            </button>
            <h1 className="checkoutTitle">Checkout</h1>
          </div>
        </div>

        <div className="checkoutContent">
          <section className="checkoutFormCard">
            <h2 className="sectionTitle mt24">Shipping Information</h2>
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading order...</p>
              </div>
            ) : error ? (
              <div className="emptyState">
                <h3>{error}</h3>
              </div>
            ) : order ? (
              <div className="formGrid">
                <div className="formField colSpan2">
                  <label>Attention To</label>
                  <input
                    name="attentionTo"
                    value={shipTo.attentionTo}
                    onChange={onChange}
                    placeholder="Full name"
                  />
                </div>
                <div className="formField colSpan2">
                  <label>Address Line 1</label>
                  <input
                    name="addressLine1"
                    value={shipTo.addressLine1}
                    onChange={onChange}
                    placeholder="Street address"
                  />
                </div>
                <div className="formField colSpan2">
                  <label>Address Line 2</label>
                  <input
                    name="addressLine2"
                    value={shipTo.addressLine2}
                    onChange={onChange}
                    placeholder="Apt, suite, etc. (optional)"
                  />
                </div>
                <div className="formField">
                  <label>City</label>
                  <input
                    name="city"
                    value={shipTo.city}
                    onChange={onChange}
                    placeholder="City"
                  />
                </div>
                <div className="formField">
                  <label>Region/State</label>
                  <input
                    name="region"
                    value={shipTo.region}
                    onChange={onChange}
                    placeholder="State"
                  />
                </div>
                <div className="formField">
                  <label>Postal Code</label>
                  <input
                    name="postalCode"
                    value={shipTo.postalCode}
                    onChange={onChange}
                    placeholder="Zip code"
                  />
                </div>
                <div className="formField">
                  <label>Country</label>
                  <input
                    name="countryCode"
                    value={shipTo.countryCode || "USA"}
                    readOnly
                    placeholder="Country"
                    className="readonly-field"
                  />
                </div>
                <div className="formField">
                  <label>Contact Phone</label>
                  <input
                    name="contactPhoneNumber"
                    value={shipTo.contactPhoneNumber}
                    onChange={onChange}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div className="formField">
                  <label>Tracking Email</label>
                  <input
                    name="trackingEmail"
                    value={shipTo.trackingEmail}
                    onChange={onChange}
                    placeholder="tracking@example.com"
                  />
                </div>
                {!hasAddress && (
                  <div className="formField colSpan2" style={{ marginTop: 8 }}>
                    <button
                      className="placeOrderBtn"
                      onClick={saveAddressLocally}
                    >
                      Save Address
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </section>

          <div className="checkoutSidebar">
            <aside className="checkoutSummaryCard">
              <h3 className="summaryTitle">Order Summary</h3>
              {order && (
                <div className="summaryItems">
                  {parsePanels().map((panel, i) => (
                    <div
                      className="summaryItem"
                      key={`${panel.code || panel.name}-${i}`}
                    >
                      <div className="summaryItemInfo">
                        <span className="summaryItemName">
                          {panel.name || panel.code}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="summaryRow">
                <span>Subtotal</span>
                <span>${(Number(order?.total) || total).toFixed(2)}</span>
              </div>
              <div className="summaryDivider" />
              <div className="summaryRow total">
                <span>Total</span>
                <span>${(Number(order?.total) || total).toFixed(2)}</span>
              </div>
              {(order?.status || "").toLowerCase().includes("pending") ? (
                <button
                  className="payNowBtn mt-3"
                  onClick={handlePayNow}
                  disabled={isPaying}
                >
                  {isPaying ? "Processing…" : "Pay Now"}
                </button>
              ) : (
                <button className="placeOrderBtn" disabled>
                  Paid
                </button>
              )}
            </aside>
          </div>
        </div>
      </div>
      <Footer />
      {showPayModal && (
        <div className="paymentModal">
          <div className="paymentModalContent">
            <div className="paymentModalHeader">
              <h3>Complete Payment</h3>
              <button
                className="modalCloseBtn"
                onClick={() => {
                  if (!isFinalizingPayment) {
                    setShowPayModal(false);
                    setIsPaying(false);
                  }
                }}
              >
                <span>×</span>
              </button>
            </div>
            <div className="paymentModalBody">
              {isFinalizingPayment && (
                <div className="paymentBlockingOverlay">
                  <div className="spinner" />
                  <div className="overlayText">Finalizing your payment...</div>
                </div>
              )}
              <div className="paymentAmount">
                <span className="amountLabel">Total Amount</span>
                <span className="amountValue">
                  ${(Number(order?.total) || total).toFixed(2)}
                </span>
              </div>
              <div
                className="paypalContainer"
                ref={paypalRef}
                style={{ pointerEvents: isFinalizingPayment ? "none" : "auto" }}
                aria-busy={isFinalizingPayment ? "true" : "false"}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserKitCheckout;
